/*
 * KYNDLE WISHLIST
 * Lightweight, dependency-free wishlist backed by localStorage (no app,
 * no account sync — matches the agreed scope). Reuses existing theme
 * mechanisms wherever possible instead of duplicating them:
 *   - fetchConfig() from assets/global.js for the add-to-cart request
 *   - PUB_SUB_EVENTS.cartUpdate (assets/pubsub.js, assets/constants.js) so
 *     the cart drawer / free-shipping bar / sticky add-to-cart bar all
 *     update exactly as if the item had been added from the product form
 *   - the cart element's own renderContents()/getSectionsToRender(), the
 *     same method assets/product-form.js calls after a normal add-to-cart
 *   - window.variantStrings (layout/theme.liquid) for "Į krepšelį" /
 *     "Išparduota" button text, so wording stays in sync with the rest of
 *     the store
 */
(function () {
  const STORAGE_KEY = 'kyndle:wishlist';

  function safeParse(raw) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  // Shopify's standard money formatting algorithm (same output as the
  // Liquid `money` filter, respecting Theme settings > money_format),
  // since fetched product JSON isn't run through Liquid.
  function formatMoney(cents) {
    const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = format.match(placeholderRegex);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholderRegex, value);
  }

  const WishlistStore = {
    getAll() {
      if (typeof localStorage === 'undefined') return [];
      return safeParse(localStorage.getItem(STORAGE_KEY));
    },
    save(items) {
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (e) {
          // Storage unavailable (private browsing quota, etc.) — the
          // wishlist simply won't persist for this session.
        }
      }
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
    },
    has(handle, variantId) {
      return this.getAll().some((item) => item.handle === handle && String(item.variantId) === String(variantId));
    },
    add(handle, variantId) {
      if (!handle || this.has(handle, variantId)) return this.getAll();
      const items = this.getAll();
      items.push({ handle: handle, variantId: String(variantId || '') });
      this.save(items);
      return items;
    },
    remove(handle, variantId) {
      const items = this.getAll().filter(
        (item) => !(item.handle === handle && String(item.variantId) === String(variantId))
      );
      this.save(items);
      return items;
    },
    toggle(handle, variantId) {
      return this.has(handle, variantId) ? this.remove(handle, variantId) : this.add(handle, variantId);
    },
  };

  function refreshButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const handle = button.dataset.wishlistHandle;
      const variantId = button.dataset.wishlistVariantId;
      const saved = WishlistStore.has(handle, variantId);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', saved ? window.wishlistStrings.remove : window.wishlistStrings.add);
    });

    const items = WishlistStore.getAll();
    const bubble = document.getElementById('wishlist-count-bubble');
    const number = document.getElementById('wishlist-count-number');
    const text = document.getElementById('wishlist-count-text');
    if (bubble && number) {
      bubble.hidden = items.length === 0;
      number.textContent = items.length > 99 ? '99+' : String(items.length);
    }
    if (text) text.textContent = `${items.length}`;
  }

  let toastElement = null;
  let toastTimeout = null;

  function getToast() {
    if (toastElement) return toastElement;
    toastElement = document.createElement('div');
    toastElement.className = 'wishlist-toast';
    toastElement.setAttribute('role', 'status');
    toastElement.setAttribute('aria-live', 'polite');
    toastElement.innerHTML = `
      <p class="wishlist-toast__text"></p>
      <button type="button" class="wishlist-toast__action" data-wishlist-toast-view hidden></button>
      <button type="button" class="wishlist-toast__close" aria-label="${escapeHtml(
        (window.wishlistStrings && window.wishlistStrings.close) || 'Close'
      )}"><span aria-hidden="true">&times;</span></button>
    `;
    document.body.appendChild(toastElement);

    toastElement.querySelector('[data-wishlist-toast-view]').addEventListener('click', () => {
      hideToast();
      document.querySelector('wishlist-drawer')?.open();
    });
    toastElement.querySelector('.wishlist-toast__close').addEventListener('click', hideToast);

    return toastElement;
  }

  function hideToast() {
    if (!toastElement) return;
    toastElement.classList.remove('is-visible');
  }

  function showToast(kind) {
    const toast = getToast();
    const strings = window.wishlistStrings || {};
    window.clearTimeout(toastTimeout);

    const text = toast.querySelector('.wishlist-toast__text');
    const action = toast.querySelector('[data-wishlist-toast-view]');

    text.textContent = kind === 'added' ? strings.added : strings.removed;
    if (kind === 'added') {
      action.hidden = false;
      action.textContent = strings.view;
    } else {
      action.hidden = true;
    }

    // Re-trigger cleanly even if a toast is already visible from a rapid
    // second click, instead of stacking a second overlapping toast.
    toast.classList.add('is-visible');
    toastTimeout = window.setTimeout(hideToast, 2800);
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-wishlist-button]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const wasSaved = WishlistStore.has(button.dataset.wishlistHandle, button.dataset.wishlistVariantId);
      WishlistStore.toggle(button.dataset.wishlistHandle, button.dataset.wishlistVariantId);
      showToast(wasSaved ? 'removed' : 'added');
    },
    true
  );

  document.addEventListener('wishlist:change', refreshButtons);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-wishlist-open]');
    if (!opener) return;
    event.preventDefault();
    document.querySelector('wishlist-drawer')?.open();
  });

  // Keep drawers mutually exclusive without modifying the cart icon/drawer
  // themselves: if the customer opens the cart while the wishlist is open,
  // close the wishlist first.
  document.addEventListener('click', (event) => {
    const cartOpener = event.target.closest('#cart-icon-bubble');
    if (!cartOpener) return;
    document.querySelector('wishlist-drawer.active')?.close();
  });

  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('#WishlistDrawer-Overlay');
      this.overlay?.addEventListener('click', this.close.bind(this));
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
    }

    connectedCallback() {
      document.addEventListener('wishlist:change', () => {
        if (this.classList.contains('active')) this.renderItems();
      });
    }

    getSectionsToRender() {
      // Mirrors CartDrawer.getSectionsToRender() (assets/cart-drawer.js) —
      // used so an add-to-cart from the wishlist refreshes the same cart
      // sections a normal product-form submit would.
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
      ];
    }

    open() {
      // Keep only one overlay/drawer active at a time.
      const cartDrawer = document.querySelector('cart-drawer.active');
      if (cartDrawer && typeof cartDrawer.close === 'function') cartDrawer.close();

      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.renderItems();
      window.setTimeout(() => this.querySelector('.drawer__close')?.focus(), 100);
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      // Only release the scroll lock this drawer itself owns; if the cart
      // drawer or menu is also open (edge case), leave the lock in place —
      // each of those already clears it correctly on its own close.
      if (!document.querySelector('cart-drawer.active') && !document.querySelector('.menu-drawer-container[open]')) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    async renderItems() {
      const list = this.querySelector('#WishlistDrawer-Items');
      const empty = this.querySelector('#WishlistDrawer-Empty');
      if (!list || !empty) return;

      const items = WishlistStore.getAll();
      if (items.length === 0) {
        list.innerHTML = '';
        empty.hidden = false;
        return;
      }
      empty.hidden = true;
      list.setAttribute('aria-busy', 'true');

      const results = await Promise.all(
        items.map((item) =>
          fetch(`${window.shopUrl || ''}/products/${item.handle}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => ({ item: item, product: product }))
            .catch(() => ({ item: item, product: null }))
        )
      );

      list.innerHTML = results.map(({ item, product }) => this.renderItem(item, product)).join('');
      list.setAttribute('aria-busy', 'false');

      list.querySelectorAll('[data-wishlist-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          WishlistStore.remove(button.dataset.handle, button.dataset.variantId);
        });
      });

      list.querySelectorAll('[data-wishlist-add-to-cart]').forEach((button) => {
        button.addEventListener('click', () => this.addToCart(button));
      });
    }

    renderItem(item, product) {
      if (!product || !product.variants || !product.variants.length) return '';

      const variant =
        product.variants.find((candidate) => String(candidate.id) === String(item.variantId)) ||
        product.variants[0];

      const image = product.featured_image || (product.images && product.images[0]) || '';
      const price = formatMoney(variant.price);
      const available = !!variant.available;
      const strings = window.variantStrings || {};

      return `
        <li class="wishlist-item">
          <a href="/products/${escapeHtml(product.handle)}" class="wishlist-item__image-link" tabindex="-1">
            ${
              image
                ? `<img src="${escapeHtml(image)}" alt="" width="90" height="90" loading="lazy" class="wishlist-item__image">`
                : ''
            }
          </a>
          <div class="wishlist-item__details">
            <a href="/products/${escapeHtml(product.handle)}" class="wishlist-item__title">${escapeHtml(
              product.title
            )}</a>
            <span class="wishlist-item__price">${price}</span>
            ${
              !available
                ? `<span class="wishlist-item__unavailable">${escapeHtml(window.wishlistStrings.unavailable)}</span>`
                : ''
            }
            <button
              type="button"
              class="button wishlist-item__add"
              data-wishlist-add-to-cart
              data-variant-id="${variant.id}"
              ${!available ? 'aria-disabled="true"' : ''}
            >
              <span>${available ? escapeHtml(strings.addToCart || '') : escapeHtml(strings.soldOut || '')}</span>
            </button>
          </div>
          <button
            type="button"
            class="wishlist-item__remove"
            data-wishlist-remove
            data-handle="${escapeHtml(item.handle)}"
            data-variant-id="${escapeHtml(item.variantId)}"
            aria-label="${escapeHtml(window.wishlistStrings.remove)}"
          >
            <span class="svg-wrapper">${window.wishlistCloseIcon || ''}</span>
          </button>
        </li>
      `;
    }

    addToCart(button) {
      if (button.getAttribute('aria-disabled') === 'true' || button.dataset.loading === 'true') return;
      button.dataset.loading = 'true';
      button.classList.add('loading');

      const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      };

      const body = { items: [{ id: button.dataset.variantId, quantity: 1 }] };
      if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
        body.sections = cartElement.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      config.body = JSON.stringify(body);

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error(response.description || response.message);
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'wishlist',
              productVariantId: button.dataset.variantId,
              cartData: response,
            });
          }

          if (cartElement && typeof cartElement.renderContents === 'function') {
            cartElement.renderContents(response);
          }
          this.close();
        })
        .catch((error) => console.error(error))
        .finally(() => {
          button.dataset.loading = 'false';
          button.classList.remove('loading');
        });
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  window.wishlistStrings = window.wishlistStrings || {
    add: 'Add to wishlist',
    remove: 'Remove from wishlist',
    unavailable: 'Unavailable',
  };
  window.wishlistCloseIcon =
    window.wishlistCloseIcon ||
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';

  document.addEventListener('DOMContentLoaded', refreshButtons);
})();
